import { useEffect, useRef, useState } from 'react'
import { HiArrowsUpDown, HiCheck } from 'react-icons/hi2'

// ────────────────────────────────────────────────────────────────
// SortByMenu — dropdown matching the Figma "Sort by" design.
// Shared by Leads (server-driven, params-backed) and Stages
// (client-side, in-memory). Consumers own the state and pass it
// down; this component just renders the trigger + menu.
// ────────────────────────────────────────────────────────────────

export type SortKey =
  | 'default'
  | 'company_asc'
  | 'company_desc'
  | 'name_asc'
  | 'name_desc'
  | 'newest'
  | 'oldest'
  | 'priority_high'
  | 'priority_low'

export const SORT_OPTIONS: Array<{ key: SortKey; label: string }> = [
  { key: 'company_asc', label: 'Company A-Z' },
  { key: 'company_desc', label: 'Company Z-A' },
  { key: 'name_asc', label: 'Name A-Z' },
  { key: 'name_desc', label: 'Name Z-A' },
  { key: 'newest', label: 'Newest First' },
  { key: 'oldest', label: 'Oldest First' },
  { key: 'priority_high', label: 'Priority (High-Low)' },
  { key: 'priority_low', label: 'Priority (Low-High)' },
]

// Priority enum stores hot=0, warm=1, cold=2 — same ordering as the
// backend. "High-Low" means hot → warm → cold.
const PRIORITY_RANK: Record<'hot' | 'warm' | 'cold', number> = {
  hot: 0,
  warm: 1,
  cold: 2,
}

// Client-side sorter for pages that hold leads in memory (e.g. Stages).
// Pages that paginate on the server (Leads) send `sort` as a query
// param instead and let the backend order the scope.
export type SortableLead = {
  id: number
  company: string
  name: string
  priority: 'hot' | 'warm' | 'cold'
  created_at_display: string
  updated_at_display: string
  // Prefer a real timestamp when available; falls back to the display
  // string for alphabetical-ish ordering if the caller doesn't have one.
  created_at?: string
  updated_at?: string
}

export function sortLeads<T extends SortableLead>(leads: T[], sort: SortKey): T[] {
  if (sort === 'default') return leads
  const copy = leads.slice()
  const cmp = (a: T, b: T): number => {
    switch (sort) {
      case 'company_asc':
        return a.company.localeCompare(b.company) || a.id - b.id
      case 'company_desc':
        return b.company.localeCompare(a.company) || b.id - a.id
      case 'name_asc':
        return a.name.localeCompare(b.name) || a.id - b.id
      case 'name_desc':
        return b.name.localeCompare(a.name) || b.id - a.id
      case 'newest':
        return b.id - a.id
      case 'oldest':
        return a.id - b.id
      case 'priority_high':
        return PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] || b.id - a.id
      case 'priority_low':
        return PRIORITY_RANK[b.priority] - PRIORITY_RANK[a.priority] || b.id - a.id
      default:
        return 0
    }
  }
  return copy.sort(cmp)
}

type Props = {
  sort: SortKey
  onChange: (next: SortKey) => void
}

export default function SortByMenu({ sort, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const select = (key: SortKey) => {
    setOpen(false)
    if (key === sort) return
    onChange(key)
  }

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 font-display text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
      >
        <HiArrowsUpDown className="h-4 w-4" aria-hidden="true" />
        Sort by
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-1 w-52 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
          <ul role="listbox">
            {SORT_OPTIONS.map((opt) => {
              const isCurrent = opt.key === sort
              return (
                <li key={opt.key}>
                  <button
                    type="button"
                    onClick={() => select(opt.key)}
                    className="flex w-full items-center gap-2 border-b border-gray-200 px-4 py-2 text-left font-display text-sm text-ink transition last:border-b-0 hover:bg-gray-50"
                  >
                    <span className="flex-1">{opt.label}</span>
                    {isCurrent && (
                      <HiCheck className="h-4 w-4 text-midnight-green" aria-hidden="true" />
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
